import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {SelectItem} from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {concatMap, filter, map, switchMap, tap} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Inject, Injectable} from '@angular/core';
import {FieldPropertyEnum} from '@docma-mm-core/field/field-property.enum';
import {Contact} from '@docma-mm-core/contact/contact';
import {TranslateService} from '@ngx-translate/core';
import {Company} from '@docma-mm-core/company/company';
import {firstValueFrom, from, Observable, Subscription} from 'rxjs';
import {DynamicFormReadyEvent, FilterSelectFieldConfig} from '@mydocma/dynamic-form';
import {FaultField} from '../fault-field';

@Injectable()
@UntilDestroy()
export class MmContactService {

  companies$ = this.projectLoaderService.contacts.pipe(
    filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
    map(m => m.data.filter(c => (c.Company.ViewPoint === this.getViewPoint() && c.Company.IsActive === true && c.Company.IsDeleted === false && c.IsActive === true && c.IsDeleted === false && c.Company.IsInsolvent === false)
      || (c.Company.ViewPoint === this.getViewPoint() && (c.Company.IsActive === false || c.Company.IsDeleted === true || c.IsActive === false || c.IsDeleted === true || c.Company.IsInsolvent === true) && c.Id === this.contact))
      .map(c => ({id: c.Company.Id, text: (c.Company.ShortName + (c.Company.Name1 ? ' - ' + c.Company.Name1 : '')) + this.getCompanyInfo(c.Company)}))
      .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], [])),
    untilDestroyed(this)
  );

  contacts$: Observable<Array<SelectItem>>;

  formReadyEvent: DynamicFormReadyEvent;
  configName: string;

  constructor(private readonly projectLoaderService: MmProjectLoaderService,
              private readonly translateService: TranslateService,
              @Inject('fieldProperty') private readonly fieldProperty: string) {

  }

  get contact() {
    return this.formReadyEvent.dynamicFormService.getFieldValue(this.configName);
  }

  get company() {
    return this.formReadyEvent.dynamicFormService.getFieldValue(this.configName + '_Company');
  }

  initContactId?: number;
  contactSubscription: Subscription;
  onCompanyChangedSubscription: Subscription;
  onContactChangedSubscription: Subscription;

  preInitialize() {
    if (this.onCompanyChangedSubscription) {
      this.onCompanyChangedSubscription.unsubscribe();
    }

    if (this.onContactChangedSubscription) {
      this.onContactChangedSubscription.unsubscribe();
    }

    if (this.contactSubscription) {
      this.contactSubscription.unsubscribe();
    }
  }

  async initialize(configName: string, contactField: FaultField, companyField: FaultField, formReadyEvent: DynamicFormReadyEvent) {
    if (!configName || !contactField || !companyField) {
      return;
    }

    this.formReadyEvent = formReadyEvent;
    this.configName = configName;

    this.initContactId = this.contact;

    const contactFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(this.configName) as FilterSelectFieldConfig;
    const companyFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(this.configName + '_Company') as FilterSelectFieldConfig;

    companyFieldConfig.source = await firstValueFrom(this.companies$);
    this.formReadyEvent.dynamicFormService.updateFieldConfig(companyFieldConfig);

    if (this.contact) {
      this.setSelectedCompany(true);
    }

    this.loadContacts(true);

    if (this.contact) {
      // console.warn(`set contact CO ${this.contact}`)
      this.formReadyEvent.dynamicFormService.updateFieldValue(contactFieldConfig.name, this.contact);
    }
    else {
      // console.warn(`set contact CO NULL`)
      this.formReadyEvent.dynamicFormService.updateFieldValue(contactFieldConfig.name, null);
    }



    this.onCompanyChangedSubscription = this.formReadyEvent.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this),
        filter((param: any) => param.config.name === this.configName + '_Company'),
        tap(company => this.loadContacts()),
        tap(company => {
          if (company.value == null && this.initContactId !== null) {
            // this.faultFormGroup.markAsDirty();
          }
        }),
        filter(company => !!company.value),
        switchMap(company => this.contacts$),
        filter(contacts => {
          const c = this.formReadyEvent.dynamicFormService.getFieldValue(contactFieldConfig.name);
          const found = (contacts?.findIndex(f => c && f.id === c) ?? 0);
          return contacts.length > 0 && found === -1;
        }),
        map(contacts => contacts[0]),
        tap(contact => {
          // console.warn(`set contact CO by company name: ${contact.text}, id: ${contact.id}`)
          this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName, contact.id);
        }),   //this.contact.setValue(contact.id)
        filter(contact => this.initContactId == null || this.initContactId !== contact.id),
        untilDestroyed(this)
      ).subscribe(contact =>  {
        // this.faultFormGroup.markAsDirty()
      }
    );

    this.onContactChangedSubscription = this.formReadyEvent.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this),
        filter((param: any) => param.config.name === this.configName),
      ).subscribe(val => {
        if (val.value != null && this.company == null) {
          this.setSelectedCompany();
        }
        else if (val.value == null) {
          this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName + '_Company', null);
          this.loadContacts();
        }
    });
  }


  loadContacts(firstLoad: boolean = false): void {
    if (this.company != null) {
      this.loadContactsByCompany(this.company);
    }
    else {
      this.loadContactsByType(firstLoad);
    }
  }

  setSelectedCompany(firstLoad: boolean = false): void {

    this.projectLoaderService.contacts.pipe(
      filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
      map(m =>  m.data.filter(c => c.Id === this.contact)),
      concatMap(contacts => from(contacts)),
      filter(contact => !!contact),
      untilDestroyed(this)
    ).subscribe(contact => {
      // console.warn(`set company name: ${contact.Company.ShortName}, id: ${contact.Company.Id}`)
      this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName + '_Company', contact.Company.Id)

      if (!firstLoad) {
        this.loadContacts();
      }
    });
  }
  loadContactsByType(firstLoad): void {

    if(!firstLoad) {
      // this.contact.setValue(null);
      if (this.formReadyEvent.dynamicFormService.getFieldValue(this.configName)) {
        this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName, null)
      }
    }

    this.contacts$  = this.projectLoaderService.contacts.pipe(
      filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
      map(m => m.data.filter(c =>
          (c.Company.ViewPoint === this.getViewPoint() && c.Company.IsDeleted === false && c.IsActive === true && c.Company.IsInsolvent === false && c.IsDeleted == false)
          || (c.Company.ViewPoint === this.getViewPoint() && (c.Company.IsDeleted === true || c.IsActive === false || c.Company.IsInsolvent === true || c.IsDeleted == true) && c.Id === this.initContactId) )
        .map(c => ({ id: c.Id, text: (c.FirstName + ' ' + c.LastName)  + this.getContactInfo(c) }))),
      untilDestroyed(this));

    this.setContactsSource();
  }

  loadContactsByCompany(companyId: number): void {
    this.contacts$  = this.projectLoaderService.contacts.pipe(
      filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
      map(m => m.data.filter(c =>
        (c.Company.Id === companyId && c.IsActive === true && c.Company.IsDeleted === false && c.Company.IsInsolvent === false && c.IsDeleted == false)
        || (c.Company.Id === companyId && (c.IsActive === false || c.Company.IsDeleted === true || c.Company.IsInsolvent === true || c.IsDeleted == true) && c.Id === this.initContactId))
        .map(c => ({ id: c.Id, text: (c.FirstName + ' ' + c.LastName)  + this.getContactInfo(c), companyId: c.Company.Id })) ),
      untilDestroyed(this));

    this.setContactsSource();
  }

  setContactsSource() {
    firstValueFrom(this.contacts$)
      .then(contacts => {
        const contactFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(this.configName) as FilterSelectFieldConfig;

        contactFieldConfig.source = contacts;
        this.formReadyEvent.dynamicFormService.updateFieldConfig(contactFieldConfig);
      });
  }

  checkCompanyByContact(c: Contact): boolean {
    return c.Company.IsActive === true && c.Company.IsDeleted === false && c.IsActive === true && c.IsDeleted === false && c.Company.IsInsolvent === false;
  }

  getCompanyInfo(c: Company): string {
    if(c.IsDeleted) {
      return " " + this.translateService.instant("MM.general.deleted");
    }
    if(!c.IsActive || c.IsInsolvent) {
      return " " + this.translateService.instant("MM.general.inactive");
    }
    return "";
  }

  getContactInfo(c: Contact): string {
    if(c.IsDeleted || c.Company?.IsDeleted) {
      return " " + this.translateService.instant("MM.general.deleted");
    }
    if(!c.IsActive || !c?.Company?.IsActive || c?.Company?.IsInsolvent) {
      return " " + this.translateService.instant("MM.general.inactive");
    }
    return "";
  }

  getViewPoint(): number {
    if (this.fieldProperty === FieldPropertyEnum.ContactCO) {
      return 1;
    }
    if (this.fieldProperty === FieldPropertyEnum.ContactCT) {
      return 2;
    }
    if (this.fieldProperty === FieldPropertyEnum.ContactSC) {
      return 4;
    }
  }
}
